import type { UpdateDocumentDto } from '@batiflow/api-client';
import { FormField } from '@batiflow/ui';
import {
  Button,
  Input,
  MessageBar,
  MessageBarBody,
  __css,
  tokens,
} from '@fluentui/react-components';
import { useState, type SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { documentErrorMessage, type DocumentDetail } from './document-model';
import { useRefreshDocuments, useUpdateDocument } from './documents-api';
import { MetadataFields } from './metadata-fields';
import { isStaleVersion, isStepUpRequired } from './model';
import type { MetadataValues } from './naming-model';

const useStyles = __css({"form":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"actions":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"fsnqrgy","Belr9w4":"f1wn3y0l"}});

const valuesOf = (document: DocumentDetail): MetadataValues => ({
  discipline: document.discipline,
  phaseType: document.phaseType,
  docType: document.docType,
  zone: document.zone ?? '',
  scale: document.scale ?? '',
});

/**
 * The fields that differ from the saved document, as the PATCH body (null clears a value). The
 * code is generated again by the server when the metadata of the pattern change.
 */
export function changesOf(
  document: DocumentDetail,
  title: string,
  values: MetadataValues,
): UpdateDocumentDto {
  const saved = valuesOf(document);
  const trimmed = title.trim();
  return {
    ...(trimmed !== '' && trimmed !== document.title ? { title: trimmed } : {}),
    ...(values.discipline !== saved.discipline ? { discipline: values.discipline } : {}),
    ...(values.phaseType !== saved.phaseType ? { phaseType: values.phaseType } : {}),
    ...(values.docType !== saved.docType ? { docType: values.docType } : {}),
    ...(values.zone.trim() !== saved.zone ? { zone: values.zone.trim() || null } : {}),
    ...(values.scale.trim() !== saved.scale ? { scale: values.scale.trim() || null } : {}),
  };
}

export interface DocumentEditFormProps {
  spaceId: string;
  document: DocumentDetail;
  onClose: () => void;
}

/**
 * Edits a document's title and metadata. Saving sends the changed fields with the document's
 * `version` as `If-Match`; the code is generated again by the server.
 */
export function DocumentEditForm({ spaceId, document, onClose }: DocumentEditFormProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const update = useUpdateDocument(spaceId);
  const refresh = useRefreshDocuments(spaceId);

  const [title, setTitle] = useState(document.title);
  const [values, setValues] = useState<MetadataValues>(() => valuesOf(document));
  const [failure, setFailure] = useState<string | null>(null);
  const titleMissing = title.trim() === '';

  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (update.isPending || titleMissing) return;
    setFailure(null);
    const data = changesOf(document, title, values);
    if (Object.keys(data).length === 0) {
      notify({ intent: 'info', message: t('edit.noChange') });
      onClose();
      return;
    }
    try {
      await update.mutateAsync({ documentId: document.id, version: document.version, data });
      await refresh();
      notify({ intent: 'success', message: t('edit.saved') });
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      if (isStaleVersion(error)) {
        notify({ intent: 'warning', message: documentErrorMessage(t, error) });
        await refresh();
        onClose();
        return;
      }
      setFailure(documentErrorMessage(t, error));
    }
  };

  return (
    <form
      noValidate
      className={styles.form}
      aria-label={t('edit.formLabel')}
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <FormField
        label={t('metadata.title')}
        required
        error={titleMissing ? t('edit.titleRequired') : null}
      >
        <Input
          maxLength={300}
          value={title}
          onChange={(_event, data) => {
            setTitle(data.value);
          }}
        />
      </FormField>
      <MetadataFields
        values={values}
        onChange={(patch) => {
          setValues((current) => ({ ...current, ...patch }));
        }}
      />
      {failure ? (
        <MessageBar intent="error" layout="multiline">
          <MessageBarBody>{failure}</MessageBarBody>
        </MessageBar>
      ) : null}
      <div className={styles.actions}>
        <Button type="submit" appearance="primary" disabledFocusable={update.isPending}>
          {update.isPending ? t('edit.saving') : t('common:actions.save')}
        </Button>
        <Button appearance="secondary" disabled={update.isPending} onClick={onClose}>
          {t('common:actions.cancel')}
        </Button>
      </div>
    </form>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/document-edit-form.griffel.css";